<template>
  <div class="completion-step">
    <div class="completion-content">
      <!-- Success Celebration -->
      <div class="celebration-section">
        <div class="celebration-icon">
          <i class="pi pi-star-fill"></i>
        </div>
        <h2 class="celebration-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="celebration-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Summary Stats -->
      <div class="summary-stats">
        <div class="stat-item">
          <div class="stat-number">{{ completedSteps }}/{{ totalSteps }}</div>
          <div class="stat-label">Шагов пройдено</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">{{ totalMeasurements }}</div>
          <div class="stat-label">Измерений собрано</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">{{ recommendationsCount }}</div>
          <div class="stat-label">Рекомендаций получено</div>
        </div>
      </div>

      <!-- Next Steps -->
      <div class="next-steps">
        <h3 class="next-steps-title">Что дальше?</h3>
        <div class="steps-list">
          <div class="step-card">
            <div class="step-icon">
              <i class="pi pi-shopping-cart"></i>
            </div>
            <div class="step-content">
              <h4>Покупки с умом</h4>
              <p>Теперь при выборе одежды вы будете видеть только подходящие размеры</p>
            </div>
          </div>
          <div class="step-card">
            <div class="step-icon">
              <i class="pi pi-bell"></i>
            </div>
            <div class="step-content">
              <h4>Персональные уведомления</h4>
              <p>Мы будем уведомлять вас о товарах в вашем размере</p>
            </div>
          </div>
          <div class="step-card">
            <div class="step-icon">
              <i class="pi pi-heart"></i>
            </div>
            <div class="step-content">
              <h4>Избранное по размеру</h4>
              <p>Ваши сохраненные товары будут фильтроваться по подходящим размерам</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="completion-actions">
        <Button @click="startShopping" class="shopping-button">
          <i class="pi pi-shopping-bag"></i>
          Начать покупки
        </Button>

        <Button @click="viewProfile" outlined class="profile-button">
          <i class="pi pi-user"></i>
          Мой профиль
        </Button>
      </div>

      <!-- Footer Note -->
      <div class="footer-note">
        <p>
          <i class="pi pi-info-circle"></i>
          Вы всегда можете обновить свои параметры в разделе профиля
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Button from 'primevue/button'
import { useSurveyModal } from '@/features/survey/store'
import { useRouter } from 'vue-router'
import type { SurveyStep } from '@/features/survey/types/survey-config'

interface Props {
  step: SurveyStep & { type: 'completion' }
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()
const router = useRouter()

const completedSteps = computed(() => surveyStore.totalSteps)
const totalSteps = computed(() => surveyStore.totalSteps)

const totalMeasurements = computed(() => {
  const data = surveyStore.surveyModal.surveyData
  let count = 0

  Object.values(data).forEach((value) => {
    if (value !== null && value !== undefined && value !== '') {
      count++
    }
  })

  return count
})

const recommendationsCount = computed(() => {
  if (!surveyStore.surveyModal.results) return 0

  const results = surveyStore.surveyModal.results
  if (!results) return 0

  // Считаем количество категорий с данными
  let count = 0
  if (results.tops) count++
  if (results.bottoms) count++
  if (results.outerwear) count++
  if (results.shoes) count++

  return count
})

function startShopping() {
  surveyStore.hideSurveyModal()
  router.push('/')
}

function viewProfile() {
  surveyStore.hideSurveyModal()
  router.push('/profile')
}
</script>

<style scoped>
.completion-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 40px 20px;
  text-align: center;
}

.completion-content {
  max-width: 600px;
  width: 100%;
}

.celebration-section {
  margin-bottom: 40px;
}

.celebration-icon {
  margin-bottom: 20px;
}

.celebration-icon i {
  font-size: 72px;
  color: #ff9500;
}

.celebration-title {
  font-size: 32px;
  font-weight: 700;
  color: #1d1d1f;
  margin: 0 0 12px 0;
  line-height: 1.2;
}

.celebration-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.summary-stats {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 40px;
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  color: white;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  font-weight: 500;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.next-steps {
  margin-bottom: 40px;
}

.next-steps-title {
  font-size: 20px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 24px 0;
}

.steps-list {
  display: grid;
  gap: 16px;
}

.step-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #007aff;
}

.step-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #007aff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-icon i {
  font-size: 20px;
  color: white;
}

.step-content {
  text-align: left;
}

.step-content h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 4px 0;
}

.step-content p {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.4;
}

.completion-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
}

.shopping-button,
.profile-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 56px;
}

.shopping-button {
  background: #007aff;
  color: white;
  border: none;
}

.shopping-button:hover {
  background: #0056cc;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.profile-button {
  background: white;
  color: #007aff;
  border: 2px solid #007aff;
}

.profile-button:hover {
  background: #007aff;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.footer-note {
  text-align: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
}

.footer-note p {
  font-size: 14px;
  color: #666;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.footer-note i {
  color: #007aff;
}

@media (max-width: 768px) {
  .celebration-title {
    font-size: 28px;
  }

  .summary-stats {
    flex-direction: column;
    gap: 16px;
  }

  .stat-number {
    font-size: 24px;
  }

  .step-card {
    flex-direction: column;
    text-align: center;
  }

  .step-content {
    text-align: center;
  }

  .completion-actions {
    gap: 12px;
  }

  .shopping-button,
  .profile-button {
    padding: 14px 20px;
    font-size: 15px;
  }
}
</style>

